import { useState } from 'react';
import {
  Badge,
  Button,
  Dropdown,
  Option,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useCommissions, useUpdateCommissionPayoutStatus } from '@/api/hooks/useReferrals';
import { COMMISSION_TYPE_LABELS, PAYOUT_STATUS_LABELS } from '@/config/services';
import type { PayoutStatus } from '@/types/referral';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  commissionCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  amountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  payoutRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: tokens.spacingVerticalS,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

const PAYOUT_BADGE_COLOR: Record<PayoutStatus, 'warning' | 'informative' | 'success'> = {
  Pending: 'warning',
  Approved: 'informative',
  Paid: 'success',
};

const PAYOUT_OPTIONS: PayoutStatus[] = ['Pending', 'Approved', 'Paid'];

interface CommissionSectionProps {
  referralId: number;
  performedBy: string;
  performedByName: string;
}

export function CommissionSection({ referralId, performedBy, performedByName }: CommissionSectionProps) {
  const styles = useStyles();
  const { data: commissions, isLoading } = useCommissions(referralId);
  const updatePayout = useUpdateCommissionPayoutStatus();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<PayoutStatus | null>(null);

  if (isLoading) return <Spinner size="tiny" label="Loading commissions..." />;

  if (!commissions || commissions.length === 0) {
    return <Text className={styles.emptyState} size={200}>No commissions yet</Text>;
  }

  const total = commissions.reduce((sum, c) => sum + c.amount, 0);

  const handleSave = async (commissionId: number) => {
    if (!selectedStatus) return;
    await updatePayout.mutateAsync({
      commissionId,
      referralId,
      data: { payoutStatus: selectedStatus },
      performedBy,
      performedByName,
    });
    setEditingId(null);
    setSelectedStatus(null);
  };

  return (
    <div className={styles.wrapper}>
      {commissions.map((c) => (
        <div key={c.id} className={styles.commissionCard}>
          <div className={styles.row}>
            <div className={styles.amountRow}>
              <Text weight="semibold" size={300}>
                {COMMISSION_TYPE_LABELS[c.type]}
              </Text>
              <Text size={400} weight="bold" style={{ color: tokens.colorPaletteGreenForeground1 }}>
                ${c.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Text>
            </div>
            <Badge appearance="tint" color={PAYOUT_BADGE_COLOR[c.payoutStatus]} size="small">
              {PAYOUT_STATUS_LABELS[c.payoutStatus]}
            </Badge>
          </div>

          {c.type === 'RevenueShare' && c.percentage != null && c.revenueBase != null && (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {(c.percentage * 100).toFixed(0)}% of ${c.revenueBase.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </Text>
          )}

          <div className={styles.payoutRow}>
            {editingId === c.id ? (
              <>
                <Dropdown
                  size="small"
                  value={selectedStatus ? PAYOUT_STATUS_LABELS[selectedStatus] : PAYOUT_STATUS_LABELS[c.payoutStatus]}
                  selectedOptions={[selectedStatus ?? c.payoutStatus]}
                  onOptionSelect={(_e, d) => setSelectedStatus(d.optionValue as PayoutStatus)}
                  style={{ minWidth: '120px' }}
                >
                  {PAYOUT_OPTIONS.map((s) => (
                    <Option key={s} value={s}>
                      {PAYOUT_STATUS_LABELS[s]}
                    </Option>
                  ))}
                </Dropdown>
                <Button
                  size="small"
                  appearance="primary"
                  disabled={updatePayout.isPending || !selectedStatus || selectedStatus === c.payoutStatus}
                  onClick={() => void handleSave(c.id)}
                >
                  {updatePayout.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() => { setEditingId(null); setSelectedStatus(null); }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                size="small"
                appearance="subtle"
                onClick={() => { setEditingId(c.id); setSelectedStatus(c.payoutStatus); }}
              >
                Change payout status
              </Button>
            )}
          </div>
        </div>
      ))}

      <div className={styles.totalRow}>
        <Text size={300} weight="semibold">Total Commissions</Text>
        <Text size={400} weight="bold" style={{ color: tokens.colorPaletteGreenForeground1 }}>
          ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      </div>
    </div>
  );
}
